import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { receivedDocApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsReceivedDocumentI,
  ReceivedDocumentI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

export const listRecibidos = async (periodo?: string) => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  const { data } = await receivedDocApi.get("", {
    params: periodo ? { periodo } : {},
    headers: { Authorization: t },
  });
  return data;
};

const getReceivedDocuments = async ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsReceivedDocumentI): Promise<
  ServiceResponseI<DataI<ReceivedDocumentI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    params.append("dateField", dateField ?? "");
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    //headers: { 'Authorization': validateToken

    const { data } = await receivedDocApi.get<
      ServiceResponseI<DataI<ReceivedDocumentI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const deleteReceivedDocument = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await receivedDocApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// libs/apis/received-document/received-document.api.ts (agrega estas dos funciones)

export const downloadAts = async (params: {
  tipo: "mensual" | "semestral";
  anio: number;
  mes?: number;
  semestre?: 1 | 2;
}): Promise<{ blob: Blob; filename: string }> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");

  const query = new URLSearchParams();
  query.append("tipo", params.tipo);
  query.append("anio", String(params.anio));
  if (params.mes) query.append("mes", String(params.mes));
  if (params.semestre) query.append("semestre", String(params.semestre));

  const response = await receivedDocApi.get(`/ats?${query.toString()}`, {
    headers: { Authorization: token },
    responseType: "blob",
  });

  const disposition = response.headers["content-disposition"] as
    | string
    | undefined;
  const match = disposition?.match(/filename="(.+)"/);
  const filename = match?.[1] ?? `Ats_${params.anio}.xlsx`;

  return { blob: response.data, filename };
};

/**
 * Trae TODOS los documentos recibidos de un rango de fechas (sin paginar),
 * para exportar a Excel al estilo EcuaFact. Primero pide el total (limit:1)
 * y luego pide todo ese total en una sola página, igual que getAtsResumen.
 */
export const getAllReceivedDocumentsByRange = async (
  dateFrom: string,
  dateTo: string,
  dateField?: string,
): Promise<ReceivedDocumentI[]> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");

  const baseParams = {
    dateField: dateField || "fechaEmision",
    dateFrom,
    dateTo,
  };

  const { data: firstPage } = await receivedDocApi.get<
    ServiceResponseI<DataI<ReceivedDocumentI>>
  >("", {
    params: { page: 1, limit: 1, ...baseParams },
    headers: { Authorization: token },
  });

  const total = firstPage?.data?.total ?? 0;
  if (total === 0) return [];

  const { data: allPage } = await receivedDocApi.get<
    ServiceResponseI<DataI<ReceivedDocumentI>>
  >("", {
    params: { page: 1, limit: total, ...baseParams },
    headers: { Authorization: token },
  });

  return allPage?.data?.dato ?? [];
};

/** Resumen rápido: reutiliza el listado paginado existente, solo pide el total. */
export const getAtsResumen = async (
  dateFrom: string,
  dateTo: string,
): Promise<{ total: number }> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const { data } = await receivedDocApi.get("", {
    params: {
      page: 1,
      limit: 1,
      dateField: "fechaEmision",
      dateFrom,
      dateTo,
    },
    headers: { Authorization: token },
  });
  return { total: data?.data?.total ?? 0 };
};

export const updateReceivedDocumentCategoria = async (
  id: string,
  categoriaGastoId: number | null,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await receivedDocApi.patch<ServiceResponseI<string>>(
      `/${id}/categoria`,
      { categoriaGastoId },
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useReceivedDocuments = ({
  sorting,
  pagination,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsReceivedDocumentI) => {
  const queryClient = useQueryClient();

  const receivedDocumentQuery = useQuery(
    [
      "sales",
      { pagination, sorting, globalFilter, dateFrom, dateTo, dateField },
    ],
    () =>
      getReceivedDocuments({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        dateField,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const deleteOneMutation = useMutation(
    (id: string) => deleteReceivedDocument(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sales"]);
      },
    },
  );

  const deleteBulkMutation = useMutation(
    (ids: string[]) => Promise.all(ids.map((id) => deleteReceivedDocument(id))),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sales"]);
      },
    },
  );

  const updateCategoriaMutation = useMutation(
    ({
      id,
      categoriaGastoId,
    }: {
      id: string;
      categoriaGastoId: number | null;
    }) => updateReceivedDocumentCategoria(id, categoriaGastoId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sales"]); // misma query key que ya usa este hook
      },
    },
  );

  return {
    receivedDocumentQuery,
    deleteOneMutation,
    deleteBulkMutation,
    updateCategoriaMutation,
  };
};



export const getReceivedDocumentPdf = async (id: string): Promise<Blob> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const { data } = await receivedDocApi.get(`/${id}/pdf`, {
    headers: { Authorization: token },
    responseType: "blob",
  });
  return data;
};


export const getReceivedDocumentXml = async (id: string): Promise<Blob> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const { data } = await receivedDocApi.get(`/${id}/xml`, {
    headers: { Authorization: token },
    responseType: "blob",
  });
  return data;
};